"use client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageContainer from "@/app/admin/_common/PageContainer";
import CuoButton from "@/app/admin/_common/CuoButton";
import GuestEditDialog from "@/app/admin/_common/guestForm/GuestEditDialog";
import { ArrowLeft, User, Pencil, Calendar } from "lucide-react";
import { formatUtcDateTime } from "@/app/_utils/dayjs";
import { useGuestDetailModel } from "./guestDetailPageModel";
import GuestDetailKpiRow from "./_cmp/GuestDetailKpiRow";
import GuestContactCard from "./_cmp/GuestContactCard";
import GuestActivityCard from "./_cmp/GuestActivityCard";
import GuestOrdersCard from "./_cmp/GuestOrdersCard";
import GuestNotesCard from "./_cmp/GuestNotesCard";
import GuestCommunicationsCard from "./_cmp/GuestCommunicationsCard";
import GuestDetailSkeleton from "./_cmp/GuestDetailSkeleton";

export default function GuestDetailPage() {
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useGuestDetailModel();
  const [editOpen, setEditOpen] = useState(false);

  const guest = data?.guest;
  const initials = guest
    ? `${guest.first_name?.[0] ?? ""}${guest.last_name?.[0] ?? ""}`.toUpperCase()
    : "";

  return (
    <PageContainer flush>
      <div className="flex h-full w-full flex-col overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b-0 bg-card/60 px-3 py-3 backdrop-blur-[2px] md:border-b md:border-border md:px-5">
          <div className="flex min-w-0 items-center gap-1.5 sm:gap-3">
            <button
              type="button"
              onClick={() => navigate("/admin/waitlist")}
              className="flex size-8 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <ArrowLeft className="size-4" />
              <span className="sr-only">Back to Waitlist</span>
            </button>
            <div className="flex size-9 flex-none items-center justify-center rounded-full bg-muted text-[13px] font-semibold text-foreground">
              {initials || <User className="size-4 text-muted-foreground" />}
            </div>
            <div className="min-w-0">
              <h1 className="m-0 truncate text-[17px] font-semibold leading-none tracking-tight text-foreground">
                {guest ? `${guest.first_name} ${guest.last_name}` : "Guest"}
              </h1>
              {guest?.signup_date && (
                <div className="m-0 mt-1 flex items-center gap-1 truncate text-[11.5px] leading-none text-muted-foreground">
                  <Calendar className="size-3 flex-none" />
                  Signed up {formatUtcDateTime(guest.signup_date, "MMM D, YYYY")}
                </div>
              )}
            </div>
          </div>
          <CuoButton
            variant="outline"
            aria-label="Edit"
            label={<span className="hidden sm:inline">Edit</span>}
            icon={<Pencil className="size-3.5 shrink-0" size={14} />}
            onClick={() => setEditOpen(true)}
            disabled={!guest}
            className="flex-none px-2 sm:px-3"
          />
        </div>

        <div className="flex-1 px-3 py-5 md:px-5">
          {isLoading || !data ? (
            <GuestDetailSkeleton />
          ) : (
            <div className="flex flex-col gap-3">
              <GuestDetailKpiRow kpis={data.kpis} />
              <div className="flex flex-col gap-3 md:flex-row">
                <div className="flex min-w-0 flex-col gap-3 md:basis-1/2">
                  <GuestContactCard guest={data.guest} />
                  <GuestActivityCard guest={data.guest} orders={data.orders} />
                  <GuestCommunicationsCard guestId={data.guest.id} />
                </div>
                <div className="flex min-w-0 flex-col gap-3 md:basis-1/2">
                  <GuestOrdersCard orders={data.orders} />
                  <GuestNotesCard guest={data.guest} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {guest && (
        <GuestEditDialog
          guest={guest}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          getData={refetch}
        />
      )}
    </PageContainer>
  );
}
